<template>
	<div class="content-wrapper">
		<div class="container flex-grow-1 container-p-y">

			<div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-6 row-gap-4">
				<div class="d-flex flex-column justify-content-center">
					<h4 class="mb-1">{{ $t('microLearning.modules.title') }}</h4>
					<p class="mb-0">{{ $t('microLearning.modules.description') }}</p>
				</div>
				<div class="d-flex align-content-center flex-wrap gap-3">
					<button type="button" class="btn btn-outline-secondary" @click="$router.push('/micro-learning')">
						<i class="ri-arrow-left-line me-1"></i>{{ $t('common.actions.back') }}
					</button>
					<router-link to="/micro-learning/modules/new">
						<button type="button" class="btn btn-primary">
							<i class="ri-add-line me-1"></i>{{ $t('microLearning.modules.createButton') }}
						</button>
					</router-link>
				</div>
			</div>

			<!-- Module Cards -->
			<div class="row g-6">
				<div class="col-xl-6 col-lg-6 col-md-6" v-for="mod in modules" :key="mod._id">
					<div class="card">
						<!-- Header -->
						<div class="card-header pb-4">
							<div class="d-flex align-items-center">
								<div class="d-flex align-items-center">
									<div class="avatar me-4">
										<span class="avatar-initial rounded-circle bg-label-primary">
											<i class="ri-lightbulb-line"></i>
										</span>
									</div>
									<div class="me-2">
										<h5 class="mb-0">{{ mod.name }}</h5>
										<div class="text-body">
											<span class="fw-medium text-muted small">{{ mod.stats.questionCount }} questions</span>
										</div>
									</div>
								</div>
								<div class="ms-auto">
									<div class="dropdown z-2">
										<button type="button" class="btn dropdown-toggle hide-arrow p-0" data-bs-toggle="dropdown" aria-expanded="false">
											<i class="ri-more-2-line ri-22px text-muted"></i>
										</button>
										<ul class="dropdown-menu dropdown-menu-end">
											<li><a class="dropdown-item waves-effect" href="javascript:void(0);" @click="editModule(mod._id)"><i class="ri-pencil-line me-2"></i>{{ $t('common.actions.edit') }}</a></li>
											<li>
												<hr class="dropdown-divider">
											</li>
											<li><a class="dropdown-item waves-effect text-danger" href="javascript:void(0);" @click="deleteModule(mod._id)"><i class="ri-delete-bin-6-line me-2"></i>{{ $t('common.actions.delete') }}</a></li>
										</ul>
									</div>
								</div>
							</div>
						</div>

						<!-- Description -->
						<div class="card-body">
							<p class="mb-0" v-if="mod.description">{{ truncate(mod.description, 120) }}</p>
							<p class="mb-0 text-muted" style="opacity: .4; font-style: italic;" v-else>Aucune description</p>
						</div>

						<!-- Stats -->
						<div class="card-body border-top">

							<div class="progress mb-4 rounded" style="height: 8px" v-if="mod.stats.totalAssignments > 0">
								<div
									class="progress-bar rounded"
									:class="completionRate(mod) >= 70 ? 'bg-success' : completionRate(mod) >= 40 ? 'bg-warning' : 'bg-danger'"
									role="progressbar"
									:style="{ width: completionRate(mod) + '%' }"
									:aria-valuenow="completionRate(mod)"
									aria-valuemin="0"
									aria-valuemax="100"
								></div>
							</div>

							<div class="d-flex align-items-center">
								<div class="d-flex align-items-center">
									<i class="ri-group-line me-1 text-muted"></i>
									<small class="text-muted">{{ $t('microLearning.modules.employeeCount', { count: mod.stats.totalAssignments }) }}</small>
								</div>
								<div class="ms-auto">
									<a href="javascript:void(0);" class="text-muted d-flex align-items-center" @click="editModule(mod._id)">
										<i class="ri-pencil-line ri-20px me-1"></i>
									</a>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Empty state -->
				<div class="col-12" v-if="modules.length === 0 && !isLoading">
					<div class="card">
						<div class="card-body text-center py-6">
							<i class="ri-lightbulb-line ri-48px text-muted mb-3 d-block"></i>
							<p class="text-muted mb-4">{{ $t('microLearning.modules.noModules') }}</p>
							<router-link to="/micro-learning/modules/new">
								<button type="button" class="btn btn-primary">
									<i class="ri-add-line me-1"></i>{{ $t('microLearning.modules.createButton') }}
								</button>
							</router-link>
						</div>
					</div>
				</div>
			</div>

		</div>
		<div class="content-backdrop fade"></div>
	</div>
</template>

<script>
import HTTP from "@/axios";
import { Tooltip } from "bootstrap";

export default {
	name: "MicroLearningModulesList",

	data() {
		return {
			modules: [],
			isLoading: false,
		};
	},

	methods: {
		editModule(moduleId) {
			this.$router.push(`/micro-learning/modules/edit/${moduleId}`);
		},

		async fetchModules() {
			this.isLoading = true;
			try {
				const response = await HTTP.get("/micro-learning/modules");
				this.modules = response.data.modules;

				this.$nextTick(() => {
					const tooltipTriggerList = this.$el.querySelectorAll('[data-bs-toggle="tooltip"]');
					tooltipTriggerList.forEach((el) => {
						new Tooltip(el);
					});
				});
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t("errors.generic");
				alert(errorMessage);
				console.error("Erreur API:", error);
			} finally {
				this.isLoading = false;
			}
		},

		async deleteModule(moduleId) {
			if (!confirm(this.$t("microLearning.modules.confirmDelete"))) return;

			try {
				await HTTP.delete(`/micro-learning/modules/${moduleId}`);
				this.fetchModules();
			} catch (error) {
				if (error?.response?.status === 403) {
					alert(this.$t("microLearning.modules.deleteError403"));
				} else {
					const errorMessage = error?.response?.data?.message || this.$t("errors.generic");
					alert(errorMessage);
				}
				console.error("Erreur API:", error);
			}
		},

		completionRate(mod) {
			if (!mod.stats.totalAssignments) return 0;
			return Math.round((mod.stats.completedAssignments / mod.stats.totalAssignments) * 100);
		},

		truncate(str, maxLen) {
			if (!str) return "";
			return str.length > maxLen ? str.substring(0, maxLen) + "..." : str;
		},

		formatDate(dateStr) {
			if (!dateStr) return "-";
			const d = new Date(dateStr);
			return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
		},
	},

	async mounted() {
		await this.fetchModules();
	},
};
</script>
